/**
 * Icon Registry
 *
 * Central mapping of semantic icon keys to Lucide icon components.
 * This abstraction allows consistent icon usage and easy library changes.
 */

import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Apple,
  ArrowDownLeft,
  ArrowDownUp,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowRightLeft,
  ArrowUpRight,
  Baby,
  Banknote,
  BarChart3,
  Bell,
  BellOff,
  Bitcoin,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  CalendarRange,
  CalendarX2,
  Camera,
  Car,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Circle,
  Clock,
  Coffee,
  CreditCard,
  Diamond,
  Dog,
  DollarSign,
  Download,
  Droplet,
  Eraser,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Film,
  Filter,
  Fuel,
  Gamepad2,
  Gem,
  Gift,
  Globe,
  GraduationCap,
  Heart,
  HeartHandshake,
  HelpCircle,
  History,
  Home,
  Inbox,
  Info,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  Link2,
  Loader2,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Maximize2,
  Minus,
  Monitor,
  Moon,
  MoreVertical,
  Music,
  Package,
  Palette,
  Pencil,
  PieChart,
  PiggyBank,
  Pill,
  Plane,
  Play,
  Plus,
  Receipt,
  ReceiptText,
  RefreshCcw,
  RefreshCw,
  Repeat,
  Repeat2,
  RotateCcw,
  Search,
  Send,
  Settings,
  Shirt,
  Square,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Split,
  Sun,
  Tag,
  ThumbsDown,
  ThumbsUp,
  Target,
  Timer,
  Train,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
  Tv,
  Undo2,
  Upload,
  User,
  Users,
  UsersRound,
  Utensils,
  Wallet,
  Wifi,
  Wrench,
  X,
  Zap,
  type LucideIcon
} from 'lucide-react';

export enum IconKey {
  // Navigation
  Dashboard = 'Dashboard',
  Transactions = 'Transactions',
  SettleUp = 'SettleUp',
  Budgets = 'Budgets',
  Goals = 'Goals',
  Categories = 'Categories',
  Accounts = 'Accounts',
  Assets = 'Assets',
  Subscriptions = 'Subscriptions',

  // Actions
  Plus = 'Plus',
  Minus = 'Minus',
  Pencil = 'Pencil',
  Trash = 'Trash',
  Search = 'Search',
  Filter = 'Filter',
  Refresh = 'Refresh',
  Upload = 'Upload',
  Download = 'Download',
  ExternalLink = 'ExternalLink',
  FitToWindow = 'FitToWindow',
  LogIn = 'LogIn',
  LogOut = 'LogOut',
  Settings = 'Settings',
  Zap = 'Zap',
  Play = 'Play',
  Eraser = 'Eraser',

  // Directional
  ChevronDown = 'ChevronDown',
  ChevronUp = 'ChevronUp',
  ChevronRight = 'ChevronRight',
  ChevronLeft = 'ChevronLeft',
  ArrowLeft = 'ArrowLeft',
  ArrowRight = 'ArrowRight',
  ArrowUpRight = 'ArrowUpRight',
  ArrowDownLeft = 'ArrowDownLeft',
  ArrowDownUp = 'ArrowDownUp',
  ArrowLeftRight = 'ArrowLeftRight',
  ArrowRightLeft = 'ArrowRightLeft',
  ChevronsUpDown = 'ChevronsUpDown',

  // Status
  Check = 'Check',
  CheckCircle = 'CheckCircle',
  CheckCircle2 = 'CheckCircle2',
  AlertCircle = 'AlertCircle',
  AlertTriangle = 'AlertTriangle',
  Loading = 'Loading',
  Lock = 'Lock',
  Clock = 'Clock',
  Info = 'Info',
  HelpCircle = 'HelpCircle',
  Circle = 'Circle',

  // Theme
  Sun = 'Sun',
  Moon = 'Moon',
  Monitor = 'Monitor',

  // UI
  X = 'X',
  Camera = 'Camera',
  Palette = 'Palette',
  Eye = 'Eye',
  EyeOff = 'EyeOff',
  MoreVertical = 'MoreVertical',
  LayoutGrid = 'LayoutGrid',
  SlidersHorizontal = 'SlidersHorizontal',
  Sparkles = 'Sparkles',
  Split = 'Split',
  Link2 = 'Link2',
  Globe = 'Globe',
  ThumbsUp = 'ThumbsUp',
  ThumbsDown = 'ThumbsDown',
  Square = 'Square',

  // Finance
  CreditCard = 'CreditCard',
  Wallet = 'Wallet',
  Receipt = 'Receipt',
  ReceiptText = 'ReceiptText',
  TrendingUp = 'TrendingUp',
  TrendingDown = 'TrendingDown',
  Building = 'Building',
  Landmark = 'Landmark',
  PiggyBank = 'PiggyBank',
  Tag = 'Tag',
  DollarSign = 'DollarSign',
  Banknote = 'Banknote',
  Diamond = 'Diamond',
  BarChart3 = 'BarChart3',
  PieChart = 'PieChart',

  // People
  User = 'User',
  Users = 'Users',
  UsersRound = 'UsersRound',

  // Time
  Calendar = 'Calendar',
  CalendarDays = 'CalendarDays',
  CalendarRange = 'CalendarRange',
  CalendarX2 = 'CalendarX2',
  Timer = 'Timer',

  // Files
  FileText = 'FileText',

  // Audit
  History = 'History',
  Repeat = 'Repeat',
  Repeat2 = 'Repeat2',
  RefreshCcw = 'RefreshCcw',
  RotateCcw = 'RotateCcw',
  Undo2 = 'Undo2',

  // Communication
  Mail = 'Mail',
  Send = 'Send',
  Bell = 'Bell',
  BellOff = 'BellOff',
  Inbox = 'Inbox',

  // Category / Asset icons (values match Lucide component names for DB compatibility)
  Activity = 'Activity',
  Apple = 'Apple',
  Baby = 'Baby',
  Bitcoin = 'Bitcoin',
  Briefcase = 'Briefcase',
  Car = 'Car',
  Coffee = 'Coffee',
  Dog = 'Dog',
  Droplet = 'Droplet',
  Film = 'Film',
  Fuel = 'Fuel',
  Gamepad2 = 'Gamepad2',
  Gift = 'Gift',
  GraduationCap = 'GraduationCap',
  Heart = 'Heart',
  Home = 'Home',
  Lightbulb = 'Lightbulb',
  Music = 'Music',
  Package = 'Package',
  Pill = 'Pill',
  Plane = 'Plane',
  Shirt = 'Shirt',
  ShoppingBag = 'ShoppingBag',
  ShoppingCart = 'ShoppingCart',
  Train = 'Train',
  Trophy = 'Trophy',
  Tv = 'Tv',
  Utensils = 'Utensils',
  Wifi = 'Wifi',
  Wrench = 'Wrench',
  Target = 'Target',
  Gem = 'Gem'
}

export const ICONS: Record<IconKey, LucideIcon> = {
  // Navigation
  [IconKey.Dashboard]: LayoutDashboard,
  [IconKey.Transactions]: Receipt,
  [IconKey.SettleUp]: HeartHandshake,
  [IconKey.Budgets]: PiggyBank,
  [IconKey.Goals]: Target,
  [IconKey.Categories]: Tag,
  [IconKey.Accounts]: Landmark,
  [IconKey.Assets]: Gem,
  [IconKey.Subscriptions]: RefreshCw,

  // Actions
  [IconKey.Plus]: Plus,
  [IconKey.Minus]: Minus,
  [IconKey.Pencil]: Pencil,
  [IconKey.Trash]: Trash2,
  [IconKey.Search]: Search,
  [IconKey.Filter]: Filter,
  [IconKey.Refresh]: RefreshCw,
  [IconKey.Upload]: Upload,
  [IconKey.Download]: Download,
  [IconKey.ExternalLink]: ExternalLink,
  [IconKey.FitToWindow]: Maximize2,
  [IconKey.LogIn]: LogIn,
  [IconKey.LogOut]: LogOut,
  [IconKey.Settings]: Settings,
  [IconKey.Zap]: Zap,
  [IconKey.Play]: Play,
  [IconKey.Eraser]: Eraser,

  // Directional
  [IconKey.ChevronDown]: ChevronDown,
  [IconKey.ChevronUp]: ChevronUp,
  [IconKey.ChevronRight]: ChevronRight,
  [IconKey.ChevronLeft]: ChevronLeft,
  [IconKey.ArrowLeft]: ArrowLeft,
  [IconKey.ArrowRight]: ArrowRight,
  [IconKey.ArrowUpRight]: ArrowUpRight,
  [IconKey.ArrowDownLeft]: ArrowDownLeft,
  [IconKey.ArrowDownUp]: ArrowDownUp,
  [IconKey.ArrowLeftRight]: ArrowLeftRight,
  [IconKey.ArrowRightLeft]: ArrowRightLeft,
  [IconKey.ChevronsUpDown]: ChevronsUpDown,

  // Status
  [IconKey.Check]: Check,
  [IconKey.CheckCircle]: CheckCircle,
  [IconKey.CheckCircle2]: CheckCircle2,
  [IconKey.AlertCircle]: AlertCircle,
  [IconKey.AlertTriangle]: AlertTriangle,
  [IconKey.Loading]: Loader2,
  [IconKey.Lock]: Lock,
  [IconKey.Clock]: Clock,
  [IconKey.Info]: Info,
  [IconKey.HelpCircle]: HelpCircle,
  [IconKey.Circle]: Circle,

  // Theme
  [IconKey.Sun]: Sun,
  [IconKey.Moon]: Moon,
  [IconKey.Monitor]: Monitor,

  // UI
  [IconKey.X]: X,
  [IconKey.Camera]: Camera,
  [IconKey.Palette]: Palette,
  [IconKey.Eye]: Eye,
  [IconKey.EyeOff]: EyeOff,
  [IconKey.MoreVertical]: MoreVertical,
  [IconKey.LayoutGrid]: LayoutGrid,
  [IconKey.SlidersHorizontal]: SlidersHorizontal,
  [IconKey.Sparkles]: Sparkles,
  [IconKey.Split]: Split,
  [IconKey.Link2]: Link2,
  [IconKey.Globe]: Globe,
  [IconKey.ThumbsUp]: ThumbsUp,
  [IconKey.ThumbsDown]: ThumbsDown,
  [IconKey.Square]: Square,

  // Finance
  [IconKey.CreditCard]: CreditCard,
  [IconKey.Wallet]: Wallet,
  [IconKey.Receipt]: Receipt,
  [IconKey.ReceiptText]: ReceiptText,
  [IconKey.TrendingUp]: TrendingUp,
  [IconKey.TrendingDown]: TrendingDown,
  [IconKey.Building]: Building2,
  [IconKey.Landmark]: Landmark,
  [IconKey.PiggyBank]: PiggyBank,
  [IconKey.Tag]: Tag,
  [IconKey.DollarSign]: DollarSign,
  [IconKey.Banknote]: Banknote,
  [IconKey.Diamond]: Diamond,
  [IconKey.BarChart3]: BarChart3,
  [IconKey.PieChart]: PieChart,

  // People
  [IconKey.User]: User,
  [IconKey.Users]: Users,
  [IconKey.UsersRound]: UsersRound,

  // Time
  [IconKey.Calendar]: Calendar,
  [IconKey.CalendarDays]: CalendarDays,
  [IconKey.CalendarRange]: CalendarRange,
  [IconKey.CalendarX2]: CalendarX2,
  [IconKey.Timer]: Timer,

  // Files
  [IconKey.FileText]: FileText,

  // Audit
  [IconKey.History]: History,
  [IconKey.Repeat]: Repeat,
  [IconKey.Repeat2]: Repeat2,
  [IconKey.RefreshCcw]: RefreshCcw,
  [IconKey.RotateCcw]: RotateCcw,
  [IconKey.Undo2]: Undo2,

  // Communication
  [IconKey.Mail]: Mail,
  [IconKey.Send]: Send,
  [IconKey.Bell]: Bell,
  [IconKey.BellOff]: BellOff,
  [IconKey.Inbox]: Inbox,

  // Category / Asset icons
  [IconKey.Activity]: Activity,
  [IconKey.Apple]: Apple,
  [IconKey.Baby]: Baby,
  [IconKey.Bitcoin]: Bitcoin,
  [IconKey.Briefcase]: Briefcase,
  [IconKey.Car]: Car,
  [IconKey.Coffee]: Coffee,
  [IconKey.Dog]: Dog,
  [IconKey.Droplet]: Droplet,
  [IconKey.Film]: Film,
  [IconKey.Fuel]: Fuel,
  [IconKey.Gamepad2]: Gamepad2,
  [IconKey.Gift]: Gift,
  [IconKey.GraduationCap]: GraduationCap,
  [IconKey.Heart]: Heart,
  [IconKey.Home]: Home,
  [IconKey.Lightbulb]: Lightbulb,
  [IconKey.Music]: Music,
  [IconKey.Package]: Package,
  [IconKey.Pill]: Pill,
  [IconKey.Plane]: Plane,
  [IconKey.Shirt]: Shirt,
  [IconKey.ShoppingBag]: ShoppingBag,
  [IconKey.ShoppingCart]: ShoppingCart,
  [IconKey.Train]: Train,
  [IconKey.Trophy]: Trophy,
  [IconKey.Tv]: Tv,
  [IconKey.Utensils]: Utensils,
  [IconKey.Wifi]: Wifi,
  [IconKey.Wrench]: Wrench,
  [IconKey.Target]: Target,
  [IconKey.Gem]: Gem
};

/**
 * Get the raw Lucide icon component for a given IconKey.
 * Useful for components that need the component reference
 * (e.g., dynamic rendering with custom sizing/style props).
 */
export function getIconComponent(key: IconKey): LucideIcon {
  return ICONS[key];
}

// Export the LucideIcon type for components that need dynamic icons
export type { LucideIcon };
